import { Chip, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { Link } from 'react-router-dom';
import { VoiceSurveyRecommendations } from './VoiceSurveyRecommendations';
import { VoiceSurveyRecording } from './VoiceSurveyRecording';
import styles from './VoiceSurveys.module.scss';
import { formatDateTime, formatDuration, statusLabel } from './surveySessionFormat';

const FACT_LABELS = {
  name: 'Name',
  search_for: 'Looking for',
  resident_name: 'Resident name',
  age: 'Age',
  gender: 'Gender',
  city: 'City',
  monthly_budget: 'Monthly budget',
};

const STOPPED_LOOKING_LABELS = {
  not_interested: 'not interested',
  moved_in: 'moved in',
  other: 'other',
};

const formatFact = (field, value) => {
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (field === 'monthly_budget') {
    return `$${Number(value).toLocaleString('en-US')}`;
  }
  if (field === 'search_for') {
    return value === 'self' ? 'Themselves' : 'Someone else';
  }
  return String(value);
};

const factLabel = (field) => FACT_LABELS[field] || field.replace(/_/g, ' ');

// What this one call learned, laid out like the person's current state above.
// "Still current" says whether a later call or the web has since replaced it.
const CallLearnings = ({ session }) => {
  const entries = Object.entries(session.collected_data || {});
  if (entries.length === 0) {
    return <p className={styles.empty}>Nothing learned on this call.</p>;
  }

  const current = Object.fromEntries((session.snapshot?.facts || []).map((fact) => [fact.field, fact]));
  const stillCurrent = (field) => {
    const fact = current[field];
    if (!fact) {
      return '-';
    }
    return fact.source === 'voice' && fact.survey_session_id === session.id ? 'Yes' : <FactSource fact={fact} />;
  };

  return (
    <Table size='small'>
      <TableHead>
        <TableRow>
          <TableCell>Field</TableCell>
          <TableCell>Value</TableCell>
          <TableCell>Still current</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {entries.map(([field, value]) => (
          <TableRow key={field}>
            <TableCell>{factLabel(field)}</TableCell>
            <TableCell>{formatFact(field, value)}</TableCell>
            <TableCell>{stillCurrent(field)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

const FactSource = ({ fact }) =>
  fact.source === 'voice' && fact.survey_session_id ? (
    <Link to={`/voice-surveys/${fact.survey_session_id}`}>call #{fact.survey_session_id}</Link>
  ) : (
    fact.source
  );

// What we know about the caller across all calls and the web, newest value per field.
const Snapshot = ({ facts }) => {
  if (facts.length === 0) {
    return <p className={styles.empty}>Nothing known about this caller yet.</p>;
  }

  return (
    <Table size='small'>
      <TableHead>
        <TableRow>
          <TableCell>Field</TableCell>
          <TableCell>Value</TableCell>
          <TableCell>Source</TableCell>
          <TableCell>Learned</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {facts.map((fact) => (
          <TableRow key={fact.field}>
            <TableCell>{factLabel(fact.field)}</TableCell>
            <TableCell>{formatFact(fact.field, fact.value)}</TableCell>
            <TableCell>
              <FactSource fact={fact} />
            </TableCell>
            <TableCell>{formatDateTime(fact.at)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

// Person-level summary that stays put above the call tabs: whether they stopped
// looking, everything we know about them and what we would
// recommend now.
export const VoiceSurveyHero = ({ session, live }) => {
  const stopped = session.stopped_looking;

  return (
    <section className={styles.hero}>
      {stopped && (
        <div className={styles.stoppedLooking}>
          <Chip
            color='warning'
            size='small'
            label={`Stopped looking: ${STOPPED_LOOKING_LABELS[stopped.reason] || stopped.reason}`}
          />
          <span>since {formatDateTime(stopped.at)}</span>
          {stopped.notes && <q>{stopped.notes}</q>}
        </div>
      )}
      <Snapshot facts={session.snapshot?.facts || []} />
      <h2>Recommended communities</h2>
      <VoiceSurveyRecommendations account={session.account} live={live} />
    </section>
  );
};

const Transcript = ({ lines }) => {
  if (lines.length === 0) {
    return <p className={styles.empty}>No transcript.</p>;
  }

  return (
    <div className={styles.transcript}>
      {lines.map((line, index) => (
        <div key={index} className={`${styles.line} ${styles[line.role]}`}>
          <span className={styles.role}>{line.role === 'agent' ? 'Agent' : 'Caller'}</span>
          <p>{line.text}</p>
        </div>
      ))}
    </div>
  );
};

// One call (a tab): recap, summary, recording, what it learned, transcript and raw webhook events.
export const VoiceSurveyDetail = ({ session }) => (
  <div className={styles.detail}>
    {session.recap?.sentence && <p className={styles.recap}>{session.recap.sentence}</p>}
    <dl className={styles.summary}>
      <div>
        <dt>Status</dt>
        <dd>{statusLabel(session.status)}</dd>
      </div>
      <div>
        <dt>Started</dt>
        <dd>{formatDateTime(session.created_at)}</dd>
      </div>
      <div>
        <dt>Duration</dt>
        <dd>{formatDuration(session.duration_seconds)}</dd>
      </div>
    </dl>

    <h2>Recording</h2>
    <VoiceSurveyRecording key={session.id} session={session} />

    <h2>Learned on this call</h2>
    <CallLearnings session={session} />

    <h2>Transcript</h2>
    <Transcript lines={session.transcript} />

    <details className={styles.events}>
      <summary>Webhook events ({session.webhook_events.length})</summary>
      {session.webhook_events.map((event) => (
        <div key={event.id}>
          <strong>{event.event_type}</strong> &middot; {formatDateTime(event.created_at)}
          <pre>{JSON.stringify(event.payload, null, 2)}</pre>
        </div>
      ))}
    </details>
  </div>
);
